import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, LayoutDashboard, Mail, FileText, Settings, Upload, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImportPanel } from "@/components/campaigns/ImportPanel";
import { CampaignDashboard } from "@/components/campaigns/CampaignDashboard";
import { CampaignWizard } from "@/components/campaigns/CampaignWizard";
import { CampaignCard } from "@/components/campaigns/CampaignCard";
import { TemplatesPanel } from "@/components/campaigns/TemplatesPanel";
import { CampaignSettings } from "@/components/campaigns/CampaignSettings";
import { CampaignDetail } from "@/components/campaigns/CampaignDetail";
import { ProspectsBase } from "@/components/campaigns/ProspectsBase";

const SUB = "rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-muted-foreground data-[state=active]:border-fuchsia-400/60 data-[state=active]:bg-fuchsia-500/20 data-[state=active]:text-fuchsia-100";

export const EmailCampaigns = () => {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [rk, setRk] = useState(0);
  const [today, setToday] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importCid, setImportCid] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [tab, setTab] = useState("dashboard");

  const load = async () => {
    try { const r = await api.get("/admin/email-campaigns"); setCamps(r.data || []); }
    catch { toast.error("Chargement impossible"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/email-campaigns/dashboard").then((r) => setToday(r.data)).catch(() => {}); }, [rk]);
  const changed = () => { load(); setRk((k) => k + 1); };

  return (
    <div className="rounded-3xl border border-fuchsia-400/30 bg-gradient-to-b from-fuchsia-500/[0.08] to-transparent p-4 sm:p-6" data-testid="email-campaigns">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex flex-wrap items-baseline gap-x-3 text-xl font-semibold text-white"><span className="flex items-center gap-2"><Mail size={20} className="text-fuchsia-300" /> Campagnes e-mail</span>
            {today && <span className="text-xs font-normal text-muted-foreground" data-testid="header-today">Aujourd'hui : {today.sent_today.toLocaleString("fr-FR")} / {today.daily_limit.toLocaleString("fr-FR")} e-mails</span>}</h2>
          <p className="text-xs text-muted-foreground">Prospects et membres · import vérifié · envoi progressif · suivi des ouvertures, clics, inscriptions et achats.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => { setDetailId(null); setTab("base"); }} className="rounded-full" data-testid="header-import-btn"><Upload size={15} className="mr-1.5" /> Importer des contacts</Button>
        <Button onClick={() => setWizard(true)} className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-400" data-testid="campaign-new-btn"><Plus size={15} className="mr-1.5" /> Nouvelle campagne</Button>
        </div>
      </div>
      {detailId ? <CampaignDetail id={detailId} onBack={() => { setDetailId(null); changed(); }} /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex h-auto flex-wrap gap-2 bg-transparent p-0">
          <TabsTrigger value="dashboard" className={SUB} data-testid="camp-sub-dashboard"><LayoutDashboard size={13} className="mr-1.5" /> Tableau de bord</TabsTrigger>
          <TabsTrigger value="base" className={SUB} data-testid="camp-sub-base"><ShieldCheck size={13} className="mr-1.5" /> Contacts vérifiés</TabsTrigger>
          <TabsTrigger value="list" className={SUB} data-testid="camp-sub-list"><Mail size={13} className="mr-1.5" /> Campagnes ({camps.length})</TabsTrigger>
          <TabsTrigger value="templates" className={SUB} data-testid="camp-sub-templates"><FileText size={13} className="mr-1.5" /> Modèles d'e-mail</TabsTrigger>
          <TabsTrigger value="settings" className={SUB} data-testid="camp-sub-settings"><Settings size={13} className="mr-1.5" /> Réglages</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><CampaignDashboard refreshKey={rk} onSent={load} onOpenCampaign={setDetailId} /></TabsContent>
        <TabsContent value="list">
          {loading ? <Loader2 className="animate-spin text-muted-foreground" /> : camps.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune campagne pour le moment. Cliquez sur « Nouvelle campagne ».</p>
          ) : <div className="space-y-3">{camps.map((c) => <CampaignCard key={c.id} c={c} onChange={changed} onOpen={setDetailId} />)}</div>}
        </TabsContent>
        <TabsContent value="base"><ProspectsBase refreshKey={rk} onChange={changed} /></TabsContent>
        <TabsContent value="templates"><TemplatesPanel /></TabsContent>
        <TabsContent value="settings"><CampaignSettings onSaved={changed} /></TabsContent>
      </Tabs>
      )}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="header-import-dialog">
          <DialogHeader><DialogTitle>Importer des contacts</DialogTitle></DialogHeader>
          {camps.length ? (
            <div className="space-y-3">
              <select value={importCid} onChange={(e) => setImportCid(e.target.value)} className="h-10 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white" data-testid="header-import-campaign">
                {camps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {importCid && <ImportPanel key={importCid} cid={importCid} onDone={() => { changed(); setImportOpen(false); }} />}
            </div>
          ) : <p className="text-sm text-muted-foreground">Créez d'abord une campagne avec « Nouvelle campagne ».</p>}
        </DialogContent>
      </Dialog>
      <CampaignWizard open={wizard} onOpenChange={setWizard} onCreated={changed} />
    </div>
  );
};

export default EmailCampaigns;
